import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, MapPin, CheckCircle2, Briefcase, ArrowRight, ArrowLeft, Loader2, ShieldCheck, Globe, ChevronDown, Search, Instagram, Upload } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import { useAuth } from '../auth/AuthContext';
import { getCountries, getProvinces, getCategories, submitVerificationDocuments, updateUserProfile } from '../../services/api';
import { beginInstagramConnect } from '../../utils/instagramConnect';
import './OnboardingWizard.scss';

const TOTAL_STEPS = 4;

const BusinessOnboardingWizard = ({ initialData, returnTo = '' }) => {
  const { t } = useTranslation('business');
  const navigate = useNavigate();
  const { currentUser, refreshUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [profileSaving, setProfileSaving] = useState(false);
  const [instagramOpening, setInstagramOpening] = useState(false);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  
  // Step 1: Identity
  const [companyName, setCompanyName] = useState(initialData?.company_name || '');
  const [role, setRole] = useState(initialData?.role || '');
  const [website, setWebsite] = useState(initialData?.website || '');
  // Step 2: Location
  const [selectedCountry, setSelectedCountry] = useState(initialData?.country ? initialData.country.toString() : '');
  const [city, setCity] = useState(initialData?.city || '');
  const [searchCountry, setSearchCountry] = useState('');
  const [provinces, setProvinces] = useState([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [searchProvince, setSearchProvince] = useState('');
  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  // Step 3: Categories & Contact
  const [phone, setPhone] = useState(initialData?.phone_number || '');
  const [selectedCategories, setSelectedCategories] = useState(initialData?.business_categories || []);
  const [rucTaxId, setRucTaxId] = useState(initialData?.ruc_tax_id || '');
  const [idDocument, setIdDocument] = useState(null);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const instagramResetRef = useRef(null);
  const hasInstagram = currentUser?.business_profile?.instagram_account_connected;
  const completionTarget = returnTo || '/business/pending-approval';

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [countriesRes, categoriesRes] = await Promise.all([
          getCountries(),
          getCategories('business')
        ]);
        setCountries(countriesRes.data);
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!selectedCountry) {
      setProvinces([]);
      setCity('');
      return;
    }

    const fetchProvinces = async () => {
      setLoadingProvinces(true);
      try {
        const response = await getProvinces(selectedCountry);
        setProvinces(response.data);
        setCity('');

        const countryObj = countries.find(c => c.id.toString() === selectedCountry);
        if (countryObj?.name === 'UNITED STATES') {
          setCity('Miami');
        }

        setSearchProvince('');
      } catch (err) {
        console.error('Error fetching provinces:', err);
      } finally {
        setLoadingProvinces(false);
      }
    };

    fetchProvinces();
  }, [selectedCountry, countries]);

  const handleCategoryToggle = (categoryId) => {
    const idStr = categoryId.toString();
    setSelectedCategories(prev => 
      prev.includes(idStr) ? prev.filter(id => id !== idStr) : [...prev, idStr]
    );
  };

  const handleInstagramConnect = () => {
    if (instagramOpening) return;
    setInstagramOpening(true);
    try {
      beginInstagramConnect(completionTarget);
    } catch (err) {
      console.error('Error opening Instagram authorization:', err);
      setInstagramOpening(false);
      return;
    }
    window.clearTimeout(instagramResetRef.current);
    instagramResetRef.current = window.setTimeout(() => setInstagramOpening(false), 4000);
  };

  const handleSubmit = async () => {
    if (!companyName || !selectedCountry || !city || !phone) {
      setError(t('onboarding.errors.required'));
      return;
    }
    setProfileSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('company_name', companyName);
      formData.append('role', role);
      formData.append('website', website);
      formData.append('country', selectedCountry);
      formData.append('city', city);
      formData.append('phone_number', phone);
      formData.append('first_name', currentUser?.first_name || 'Business');
      formData.append('last_name', currentUser?.last_name || 'Representative');
      selectedCategories.forEach(cat => formData.append('business_categories', cat));
      await updateUserProfile(formData);
      const refreshedUser = await refreshUser();
      const instagramConnected = refreshedUser?.business_profile?.instagram_account_connected || hasInstagram;

      if (instagramConnected) {
        navigate(completionTarget);
      } else {
        setCurrentStep(4);
      }
    } catch (err) {
      console.error('Error updating business profile:', err);
      setError(t('onboarding.errors.save_error'));
    } finally {
      setProfileSaving(false);
    }
  };

  useEffect(() => () => window.clearTimeout(instagramResetRef.current), []);

  const handleManualVerificationSubmit = async () => {
    if (!rucTaxId || !idDocument) {
      setError(t('onboarding.step4.manual_required', 'RUC/Tax ID and representative ID document are required.'));
      return;
    }

    setManualSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('ruc_tax_id', rucTaxId);
      formData.append('id_document', idDocument);
      await submitVerificationDocuments(formData);
      await refreshUser();
      navigate(completionTarget, { replace: true });
    } catch (err) {
      console.error('Error submitting business verification documents:', err);
      setError(err.response?.data?.error || t('onboarding.errors.save_error'));
    } finally {
      setManualSubmitting(false);
    }
  };

  const usCountry = countries.find(c => c.name === 'UNITED STATES');
  const latinAmericaCountries = countries.filter(c => c.name !== 'UNITED STATES');
  const filteredLatinCountries = latinAmericaCountries.filter(c =>
    c.name.toLowerCase().includes(searchCountry.toLowerCase())
  );

  return (
    <div className="wizard-container">
      <div className="wizard-progress">
        <div className="progress-bar" style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">

        {/* ── STEP 1: Brand Identity ── */}
        {currentStep === 1 && (
          <motion.div
            key="biz-step1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step1.title')}</h2>
              <p>{t('onboarding.step1.subtitle')}</p>
            </div>

            <div className="instagram-connect-box">
              <div className="ig-icon-wrapper" style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
              }}>
                <Building2 size={32} color="#c9a227" />
              </div>
              <h3>{t('onboarding.step1.card_title')}</h3>
              <p className="ig-subtitle">{t('onboarding.step1.card_subtitle')}</p>

              <div className="brand-inputs">
                <div className="form-group">
                  <label>{t('onboarding.step1.form.company_name')}</label>
                  <div className="input-with-icon">
                    <Building2 size={16} />
                    <input
                      type="text"
                      className="wizard-input"
                      placeholder={t('onboarding.step1.form.company_placeholder')}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('onboarding.step1.form.website')}</label>
                  <div className="input-with-icon">
                    <Globe size={16} />
                    <input
                      type="text"
                      className="wizard-input"
                      placeholder={t('onboarding.step1.form.website_placeholder')}
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('onboarding.step1.form.role')}</label>
                  <div className="input-with-icon">
                    <Briefcase size={16} />
                    <input
                      type="text"
                      className="wizard-input"
                      placeholder={t('onboarding.step1.form.role_placeholder')}
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="security-disclaimer">
              <ShieldCheck size={20} className="text-gold" />
              <div className="disclaimer-text">
                <strong>{t('onboarding.step1.disclaimer').split('.')[0]}.</strong> {t('onboarding.step1.disclaimer').split('.').slice(1).join('.')}
              </div>
            </div>

            <div className="wizard-actions">
              <button
                className="btn-next"
                disabled={!companyName}
                onClick={() => setCurrentStep(2)}
              >
                {t('onboarding.buttons.continue')} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* ── STEP 2: Location ── */}
        {currentStep === 2 && (
          <motion.div
            key="biz-step2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step2.title')}</h2>
              <p>{t('onboarding.step2.subtitle')}</p>
            </div>

            <div className="form-group">
              <label>{t('onboarding.step2.form.country')}</label>
              <div className="input-with-icon">
                <MapPin size={18} />
                <input
                  type="text"
                  className="wizard-input search-input"
                  placeholder={t('onboarding.step2.form.country_placeholder')}
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="country-grid-wrapper">
                {usCountry && (
                  <div className="special-country-section mb-4">
                    <button
                      type="button"
                      className={`country-card ${selectedCountry === usCountry.id.toString() ? 'selected' : ''}`}
                      onClick={() => setSelectedCountry(usCountry.id.toString())}
                    >
                      <div className="country-name">{usCountry.name}</div>
                      {selectedCountry === usCountry.id.toString() && <CheckCircle2 size={16} className="text-gold" />}
                    </button>
                    <div className="section-divider mt-4 mb-2">
                      <span>{t('onboarding.step2.form.region_rest')}</span>
                    </div>
                  </div>
                )}

                <div className="country-grid">
                  {filteredLatinCountries.length > 0 ? filteredLatinCountries.map(country => (
                    <button
                      type="button"
                      key={country.id}
                      className={`country-card ${selectedCountry === country.id.toString() ? 'selected' : ''}`}
                      onClick={() => setSelectedCountry(country.id.toString())}
                    >
                      <div className="country-name">{country.name}</div>
                      {selectedCountry === country.id.toString() && <CheckCircle2 size={16} className="text-gold" />}
                    </button>
                  )) : (
                    <p className="no-results">{t('onboarding.step2.no_countries')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>
                {selectedCountry
                  ? (countries.find(c => c.id.toString() === selectedCountry)?.division_name || t('onboarding.step2.form.city'))
                  : t('onboarding.step2.form.city')
                }
              </label>
              <div className="custom-select-container">
                <button
                  type="button"
                  className={`custom-select-trigger ${!selectedCountry ? 'disabled' : ''}`}
                  onClick={() => selectedCountry && setShowProvinceDropdown(!showProvinceDropdown)}
                >
                  <MapPin size={18} />
                  <span>
                    {city || (selectedCountry
                      ? t('onboarding.step2.form.select_division', {
                        division: countries.find(c => c.id.toString() === selectedCountry)?.division_name || t('onboarding.step2.form.city')
                      })
                      : t('onboarding.step2.form.city_placeholder'))}
                  </span>
                  <ChevronDown size={18} className={`chevron ${showProvinceDropdown ? 'open' : ''}`} />
                </button>

                <AnimatePresence>
                  {showProvinceDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="custom-select-dropdown"
                    >
                      <div className="dropdown-search">
                        <Search size={14} />
                        <input
                          type="text"
                          placeholder={t('onboarding.step2.form.search_division')}
                          value={searchProvince}
                          onChange={(e) => setSearchProvince(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="dropdown-options">
                        {provinces
                          .filter(p => p.name.toLowerCase().includes(searchProvince.toLowerCase()))
                          .map(province => (
                            <button
                              type="button"
                              key={province.id}
                              className={`dropdown-option ${city === province.name ? 'selected' : ''}`}
                              onClick={() => {
                                setCity(province.name);
                                setShowProvinceDropdown(false);
                                setSearchProvince('');
                              }}
                            >
                              {province.name}
                            </button>
                          ))
                        }
                        {loadingProvinces && <div className="dropdown-loading"><Loader2 className="spin" size={16} /></div>}
                        {!loadingProvinces && provinces.length === 0 && <div className="dropdown-no-results">{t('onboarding.step2.form.no_divisions')}</div>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="wizard-actions dual">
              <button className="btn-back" onClick={() => setCurrentStep(1)}>
                <ArrowLeft size={16} /> {t('onboarding.buttons.back')}
              </button>
              <button
                className="btn-next"
                disabled={!selectedCountry || !city}
                onClick={() => setCurrentStep(3)}
              >
                {t('onboarding.buttons.continue')} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* ── STEP 3: Categories & Contact ── */}
        {currentStep === 3 && (
          <motion.div
            key="biz-step3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step3.title')}</h2>
              <p>{t('onboarding.step3.subtitle')}</p>
            </div>

            <div className="form-group">
              <label>{t('onboarding.step3.form.industry')}</label>
              <div className="categories-pills">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`pill ${selectedCategories.includes(cat.id.toString()) ? 'active' : ''}`}
                    onClick={() => handleCategoryToggle(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>{t('onboarding.step3.form.phone')}</label>
              <PhoneInput
                country={selectedCountry ? countries.find(c => c.id.toString() === selectedCountry)?.code?.toLowerCase() : 'pa'}
                value={phone}
                onChange={setPhone}
                containerClass="phone-input-container wizard-phone"
                inputClass="wizard-input"
              />
            </div>

            {error && <div className="wizard-error">{error}</div>}

            <div className="wizard-actions dual">
              <button className="btn-back" onClick={() => setCurrentStep(2)}>
                <ArrowLeft size={16} /> {t('onboarding.buttons.back')}
              </button>
              <button
                className="btn-submit"
                disabled={!phone || selectedCategories.length === 0 || profileSaving}
                onClick={handleSubmit}
              >
                {profileSaving ? <Loader2 className="spin" size={20} /> : t('onboarding.buttons.finish')}
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Instagram Graph connection */}
        {currentStep === 4 && (
          <motion.div
            key="biz-step4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
            className="wizard-step"
          >
            <div className="step-header">
              <h2>{t('onboarding.step4.title')}</h2>
              <p>{t('onboarding.step4.subtitle')}</p>
            </div>

            <div className="instagram-connect-box">
              <div className="ig-icon-wrapper">
                <Instagram size={40} />
              </div>
              <h3>{t('onboarding.step4.connect_title')}</h3>
              <p className="ig-subtitle">{t('onboarding.step4.connect_subtitle')}</p>

              <button
                className="btn-instagram-onboarding"
                onClick={handleInstagramConnect}
                disabled={instagramOpening}
              >
                {instagramOpening ? <Loader2 className="spin" size={20} /> : <Instagram size={20} />}
                {instagramOpening ? t('onboarding.step4.connecting') : t('onboarding.step4.connect_button')}
              </button>
            </div>

            <div className="section-divider mt-4 mb-4">
              <span>{t('onboarding.step4.or_manual', 'Or verify with business documents')}</span>
            </div>

            <div className="instagram-connect-box">
              <div className="ig-icon-wrapper" style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
              }}>
                <ShieldCheck size={36} />
              </div>
              <h3>{t('onboarding.step4.manual_title', 'Verify with RUC and ID')}</h3>
              <p className="ig-subtitle">{t('onboarding.step4.manual_subtitle', 'Submit your business tax ID and a representative ID document for admin review.')}</p>

              <div className="brand-inputs">
                <div className="form-group">
                  <label>{t('onboarding.step4.ruc_label', 'RUC / Tax ID')}</label>
                  <div className="input-with-icon">
                    <Briefcase size={16} />
                    <input
                      type="text"
                      className="wizard-input"
                      value={rucTaxId}
                      onChange={(e) => setRucTaxId(e.target.value)}
                      placeholder={t('onboarding.step4.ruc_placeholder', 'Enter your RUC or Tax ID')}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('onboarding.step4.id_document_label', 'Representative ID document')}</label>
                  <label className="btn-instagram-onboarding" style={{ cursor: 'pointer' }}>
                    <Upload size={20} />
                    {idDocument
                      ? idDocument.name
                      : t('onboarding.step4.id_document_button', 'Upload ID document')}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setIdDocument(e.target.files?.[0] || null)}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              {error && <div className="wizard-error">{error}</div>}

              <button
                type="button"
                className="btn-submit manual-submit-button"
                onClick={handleManualVerificationSubmit}
                disabled={manualSubmitting || !rucTaxId || !idDocument}
                aria-busy={manualSubmitting}
              >
                {manualSubmitting ? <Loader2 className="spin" size={20} /> : <ShieldCheck size={18} />}
                {manualSubmitting
                  ? t('onboarding.step4.manual_submitting', 'Enviando a revisión...')
                  : t('onboarding.step4.manual_submit', 'Enviar a revisión')}
              </button>
            </div>

            <div className="security-disclaimer">
              <ShieldCheck size={20} className="text-gold" />
              <div className="disclaimer-text">
                <strong>{t('onboarding.step4.disclaimer').split('.')[0]}.</strong> {t('onboarding.step4.disclaimer').split('.').slice(1).join('.')}
              </div>
            </div>

            <div className="wizard-actions">
              <button className="btn-back" onClick={() => setCurrentStep(3)}>
                <ArrowLeft size={16} /> {t('onboarding.buttons.back')}
              </button>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default BusinessOnboardingWizard;

